import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchStaff,
  fetchShifts,
  createShift,
  updateShift,
  deleteShift,
} from "../services/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // 0 = Monday
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const EMPTY = { staff_id: "", shift_date: "", start_time: "09:00", end_time: "17:00", area: "", notes: "" };

export default function SchedulePage() {
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [st, sh] = await Promise.all([
        fetchStaff(),
        fetchShifts(fmt(weekStart), fmt(addDays(weekStart, 6))),
      ]);
      setStaff(st.filter((s) => s.active !== false));
      setShifts(sh);
    } catch (e) {
      setError("Could not load the schedule.");
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    load();
  }, [load]);

  const areas = useMemo(
    () => Array.from(new Set(shifts.map((s) => s.area).filter(Boolean))),
    [shifts]
  );

  // Role filter — the roster gets long with many staff, so let the manager
  // narrow to one role (front desk, housekeeping, kitchen…).
  const roles = useMemo(
    () => Array.from(new Set(staff.map((s) => s.role).filter(Boolean))),
    [staff]
  );
  const shownStaff = roleFilter === "all" ? staff : staff.filter((s) => s.role === roleFilter);

  const cellShifts = (staffId, dayStr) =>
    shifts
      .filter((s) => s.staff_id === staffId && s.shift_date === dayStr)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));

  const openNew = (staffId, dayStr) =>
    setForm({ ...EMPTY, staff_id: staffId, shift_date: dayStr });
  const openEdit = (sh) =>
    setForm({
      id: sh.id,
      staff_id: sh.staff_id,
      shift_date: sh.shift_date,
      start_time: sh.start_time,
      end_time: sh.end_time,
      area: sh.area || "",
      notes: sh.notes || "",
    });

  const save = async () => {
    if (!form.staff_id || !form.shift_date) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        staff_id: Number(form.staff_id),
        shift_date: form.shift_date,
        start_time: form.start_time,
        end_time: form.end_time,
        area: form.area || null,
        notes: form.notes || null,
      };
      if (form.id) await updateShift(form.id, payload);
      else await createShift(payload);
      setForm(null);
      await load();
    } catch (e) {
      setError("Could not save the shift. Times must be HH:MM and end after start.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form.id) return;
    setSaving(true);
    try {
      await deleteShift(form.id);
      setForm(null);
      await load();
    } catch (e) {
      setError("Could not delete the shift.");
    } finally {
      setSaving(false);
    }
  };

  const weekLabel = `${fmt(weekStart)} → ${fmt(addDays(weekStart, 6))}`;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Staff schedule</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="btn" onClick={() => setWeekStart(addDays(weekStart, -7))}>&larr; Prev</button>
          <button className="btn" onClick={() => setWeekStart(startOfWeek(new Date()))}>This week</button>
          <button className="btn" onClick={() => setWeekStart(addDays(weekStart, 7))}>Next &rarr;</button>
        </div>
      </div>
      <p style={{ color: "#667", marginTop: 4 }}>
        {weekLabel}. Assign who works which day, their hours, and the area they cover.
      </p>
      {roles.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "6px 0 12px" }}>
          <button type="button" onClick={() => setRoleFilter("all")} style={roleChip(roleFilter === "all")}>
            All ({staff.length})
          </button>
          {roles.map((r) => (
            <button key={r} type="button" onClick={() => setRoleFilter(r)} style={roleChip(roleFilter === r)}>
              {r} ({staff.filter((s) => s.role === r).length})
            </button>
          ))}
        </div>
      )}
      {error && <div style={{ color: "#b00", marginBottom: 8 }}>{error}</div>}
      {loading ? (
        <p>Loading&hellip;</p>
      ) : staff.length === 0 ? (
        <p>No staff yet. Add staff first, then schedule their shifts.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 760 }}>
            <thead>
              <tr>
                <th style={th}>Staff</th>
                {days.map((d, i) => (
                  <th key={i} style={th}>
                    {DAYS[i]}
                    <br />
                    <span style={{ fontWeight: 400, fontSize: 12, color: "#889" }}>{fmt(d).slice(5)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownStaff.map((s) => (
                <tr key={s.id}>
                  <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap" }}>
                    {s.name || s.email}
                    <br />
                    <span style={{ fontWeight: 400, fontSize: 12, color: "#889" }}>{s.role}</span>
                  </td>
                  {days.map((d, i) => {
                    const ds = fmt(d);
                    const cs = cellShifts(s.id, ds);
                    return (
                      <td key={i} style={td}>
                        {cs.map((sh) => (
                          <button key={sh.id} onClick={() => openEdit(sh)} style={chip} title={sh.notes || ""}>
                            {sh.start_time}&ndash;{sh.end_time}{sh.area ? ` · ${sh.area}` : ""}
                          </button>
                        ))}
                        <button onClick={() => openNew(s.id, ds)} style={addBtn}>+</button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <div style={overlay} onClick={() => !saving && setForm(null)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{form.id ? "Edit shift" : "New shift"}</h3>
            <label style={lbl}>
              Staff
              <select value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} style={inp}>
                <option value="">(choose)</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{(s.name || s.email)} ({s.role})</option>
                ))}
              </select>
            </label>
            <label style={lbl}>
              Date
              <input type="date" value={form.shift_date} onChange={(e) => setForm({ ...form, shift_date: e.target.value })} style={inp} />
            </label>
            <div style={{ display: "flex", gap: 10 }}>
              <label style={{ ...lbl, flex: 1 }}>
                Start
                <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} style={inp} />
              </label>
              <label style={{ ...lbl, flex: 1 }}>
                End
                <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} style={inp} />
              </label>
            </div>
            <label style={lbl}>
              Area / zone (optional)
              <input list="sched-areas" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. Pool, Bar, Floor 1" style={inp} />
              <datalist id="sched-areas">{areas.map((a) => <option key={a} value={a} />)}</datalist>
            </label>
            <label style={lbl}>
              Notes (optional)
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={inp} />
            </label>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
              <div>{form.id && <button className="btn" onClick={remove} disabled={saving} style={{ background: "#b00", color: "#fff" }}>Delete</button>}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
                <button className="btn" onClick={save} disabled={saving || !form.staff_id}>{saving ? "Saving…" : "Save"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const roleChip = (active) => ({
  fontSize: 12.5, padding: "4px 12px", borderRadius: 20, cursor: "pointer", textTransform: "capitalize",
  border: active ? "1.5px solid #0F6E56" : "1px solid #e2e6ea",
  background: active ? "#E1F5EE" : "#fff",
  color: active ? "#0F6E56" : "#5b6472", fontWeight: active ? 700 : 500,
});
const th = { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e3e8ef", fontSize: 13 };
const td = { padding: "6px 8px", borderBottom: "1px solid #eef2f6", verticalAlign: "top", minWidth: 90 };
const chip = { display: "block", width: "100%", textAlign: "left", margin: "2px 0", padding: "3px 6px", border: "1px solid #cdd8e3", borderRadius: 6, background: "#eef5ff", cursor: "pointer", fontSize: 12 };
const addBtn = { display: "block", marginTop: 2, padding: "1px 8px", border: "1px dashed #b9c4d0", borderRadius: 6, background: "transparent", cursor: "pointer", color: "#5a6b7b", fontSize: 13 };
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 };
const modal = { background: "#fff", borderRadius: 12, padding: 20, width: 380, maxWidth: "100%", boxShadow: "0 10px 40px rgba(0,0,0,.2)" };
const lbl = { display: "block", fontSize: 13, fontWeight: 600, margin: "8px 0 0", color: "#334" };
const inp = { display: "block", width: "100%", marginTop: 4, padding: "7px 8px", border: "1px solid #cdd8e3", borderRadius: 8, fontWeight: 400, boxSizing: "border-box" };
